import {
  Skeleton,
  SkeletonBlock,
  SkeletonContent,
  SkeletonLine,
} from '@one-thd/sui-atomic-components';
import React from 'react';
import { ReturnLabelProvider } from '../../../contexts/ReturnLabelContext';
import { IDeliveryTrackerData } from '../../../models/common';
import { IPHDeliveryTracker } from '../../../models/delivery-tracker';
import { IFulfillmentGroup } from '../../../models/order-details';
import { getReturnStatus } from '../../../utils/order-details/OrderDetailsUtils';
import { retrieveTrackingNumbers } from '../../../utils/returns/returns-helper';
import { ProductImages } from '../../shared/ProductImages';
import { ReturnStatus } from '../card-view/ReturnStatus';
import { PHOrderActions, PHOrderBadge, OrderStatus } from './index';

import '../../shared/ButtonFix.scss';

export const PHOrderDetailsAndActions = ({
  emailId,
  fulfillmentGroup,
  net,
  orderDetailsLoaded = false,
  orderNumber,
  salesDate,
  variant = 'card',
  isPTCOrder,
}: IPHDeliveryTracker) => {
  const preShip =
    fulfillmentGroup?.lineItems?.[0]?.statusDescription === 'Being Processed';

  const fulfillmentType = fulfillmentGroup?.fulfillmentType ?? '';

  const trackingNumbers = retrieveTrackingNumbers(
    fulfillmentGroup ?? ({} as IFulfillmentGroup),
  );

  const { isInitiatedReturn, isReturnedMultiReturn, isSoris } = getReturnStatus(
    fulfillmentGroup ?? ({} as IFulfillmentGroup),
  );

  if (fulfillmentGroup === undefined || !orderDetailsLoaded) {
    return (
      <div className="sui-flex sui-gap-6">
        {variant === 'card' ? (
          <>
            <div className="sui-flex sui-flex-col sui-gap-3 sui-w-2/4">
              <SkeletonLine variant="multi" numberOfLines={2} fullWidth />
              <div className="sui-flex sui-gap-4">
                {[...Array(4)].map((__j, key) => (
                  <SkeletonBlock key={key} height={12} width={12} />
                ))}
              </div>
            </div>
            <div className="sui-w-2/4">
              <SkeletonLine variant="multi" numberOfLines={2} fullWidth />
            </div>
          </>
        ) : (
          <Skeleton disablePadding orientation="horizontal">
            <SkeletonContent
              disablePadding
              initialSize="2/4"
              style={{ width: '195px', height: '50px' }}
            >
              <SkeletonLine variant="single" />
              <SkeletonLine variant="single" />
            </SkeletonContent>
            {[...Array(4)].map((__j, key) => (
              <SkeletonBlock key={key} height={12} width={12} />
            ))}
          </Skeleton>
        )}
      </div>
    );
  }

  if (
    fulfillmentGroup.cancellationStatus !== 'OUT_OF_STOCK' &&
    (fulfillmentGroup.lineItems ?? [])?.length === 1 &&
    fulfillmentGroup?.lineItems?.[0]?.statusDescription === 'Canceled'
  ) {
    return null;
  }

  const isGiftCard = fulfillmentGroup?.lineItems?.some(
    (item) => item.isGiftCard,
  );

  return (
    <ReturnLabelProvider
      emailId={emailId}
      orderId={orderNumber ?? ''}
      trackingNumbers={trackingNumbers}
      isGuest={false}
      net={net}
    >
      <div
        className={`sui-flex sui-flex-col md:sui-flex-row sui-gap-0 sui-w-full ${
          variant === 'card' ? 'sui-items-start' : 'sui-items-center'
        }`}
      >
        <div
          className={`sui-flex sui-w-full ${
            variant === 'card' ? 'sui-flex-col' : 'sui-flex-row'
          } sui-gap-0 sui-min-w-[330px] ${
            variant === 'card' ? 'md:sui-min-w-[422px]' : 'md:sui-min-w-[516px]'
          }`}
        >
          {(() => {
            if (isReturnedMultiReturn) {
              return (
                <ReturnStatus
                  fg={fulfillmentGroup ?? ({} as IFulfillmentGroup)}
                  variant={variant}
                />
              );
            }
            return (
              <div
                className={`sui-flex sui-flex-col ${
                  variant === 'card' ? '' : 'sui-gap-[8px]'
                } ${variant === 'card' ? '' : 'sui-min-w-[260px]'}`}
              >
                <OrderStatus
                  deliveryTrackerData={fulfillmentGroup?.deliveryTrackerData}
                  fulfillmentGroup={fulfillmentGroup}
                  fulfillmentType={fulfillmentType}
                  page="purchase-history"
                  variant={variant}
                  salesDate={salesDate}
                  deliveryTrackerLoaded={
                    fulfillmentGroup?.deliveryTrackerLoaded
                  }
                />

                {!isGiftCard && (
                  <PHOrderBadge
                    deliveryTrackerData={
                      fulfillmentGroup?.deliveryTrackerData as IDeliveryTrackerData | null
                    }
                    deliveryTrackerLoaded={
                      fulfillmentGroup?.deliveryTrackerData !== undefined
                    }
                    fulfillmentGroup={fulfillmentGroup}
                    fulfillmentType={fulfillmentType}
                    preShip={preShip}
                  />
                )}
              </div>
            );
          })()}

          <div className={`${variant === 'card' ? '' : 'sui-min-w-[300px]'}`}>
            <ProductImages
              isLoading={false}
              lineItems={fulfillmentGroup?.lineItems ?? []}
              maxShow={5}
              variant="medium"
              hideCanceled={
                fulfillmentGroup?.cancellationStatus !== 'OUT_OF_STOCK'
              }
            />
          </div>
        </div>

        <PHOrderActions
          deliveryTrackerData={
            fulfillmentGroup?.deliveryTrackerData as IDeliveryTrackerData | null
          }
          deliveryTrackerError={fulfillmentGroup?.deliveryTrackerData === null}
          emailId={emailId}
          fulfillmentGroup={fulfillmentGroup as IFulfillmentGroup}
          fulfillmentType={fulfillmentType}
          isInitiatedReturn={isInitiatedReturn}
          isSoris={isSoris}
          net={net}
          orderNumber={orderNumber ?? ''}
          trackingNumbers={trackingNumbers}
          variant={variant}
          isPTCOrder={isPTCOrder}
        />
      </div>
    </ReturnLabelProvider>
  );
};
